/**
 * «Probá tu bot» como pantalla del panel (GKTIBO-75, regla C7).
 *
 * La misma pantalla que el dueño usa antes de salir en vivo queda accesible
 * después. No es una mejora: es criterio de aceptación, y por una razón de
 * producto — el conocimiento del bot cambia, y cada vez que cambia hay que
 * poder volver a probarlo sin esperar a que le escriba un cliente.
 *
 * La pantalla en sí vive en `alta/ProbarElBot` porque nació ahí y es la misma
 * pieza; duplicarla sería garantizar que las dos se separen (L34).
 *
 * ## Por qué dice a qué negocio le habla (GKTIBO-116)
 *
 * `activeTenantId` es, literalmente, «el primero de la lista»
 * (`useTenantContext.ts:9`). Sin decirlo ni dejar elegir, el 2026-09-23 esta
 * pantalla conversó con el bot **productivo** —35.000 eventos diarios— porque
 * era el primero, y nada permitía notarlo.
 *
 * Para un dueño con un solo negocio el nombre no es ruido: es tranquilizador.
 * Con varios, el selector es la diferencia entre probar y adivinar.
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { AppShell } from '../components/AppShell';
import { useTenantContext } from '../hooks/useTenantContext';
import { api } from '../lib/api.ts';
import { ProbarElBot } from './alta/ProbarElBot';
import { probar } from './alta/textos-probar';

export function PaginaDeProbar() {
  const { activeTenantId } = useTenantContext();
  const [elegido, setElegido] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['negocios-para-probar'],
    queryFn: () => api.tenants.list(),
  });

  const negocios = data?.tenants ?? [];
  const tenantId = elegido ?? activeTenantId;
  const actual = negocios.find((n) => n.tenantId === tenantId);

  if (!tenantId) {
    // Sin tenant activo no hay bot que probar. Se dice, en vez de mostrar un
    // chat que va a fallar en el primer mensaje.
    return (
      <AppShell>
        <div className="probar-columna" data-testid="probar-sin-tenant">
          <h1 className="card-title">{probar.titulo}</h1>
          <p className="text-sm text-content-secondary">{probar.sinTenant}</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      {/* Ancho acotado: un chat a lo ancho de un monitor deja las burbujas
          nadando y se lee peor que en el teléfono. */}
      <div className="probar-columna">
        <div className="probar-de-quien">
          {negocios.length > 1 ? (
            <>
              <label htmlFor="probar-negocio">{probar.deQuien}</label>
              <select
                id="probar-negocio"
                value={tenantId}
                onChange={(e) => setElegido(e.target.value)}
              >
                {negocios.map((n) => (
                  <option key={n.tenantId} value={n.tenantId}>
                    {n.name}
                  </option>
                ))}
              </select>
            </>
          ) : (
            <p>
              {probar.deQuien} <strong>{actual?.name ?? tenantId}</strong>
            </p>
          )}
        </div>

        {/*
         * `key` con el tenant: cambiar de negocio **desmonta y vuelve a montar**
         * el chat, así que arranca con una sesión nueva y vacía.
         *
         * No es un detalle de React. La memoria es por sesión: arrastrarla
         * haría que el bot de un negocio conteste con el contexto de otro, que
         * es lo más parecido a una fuga entre tenants que puede tener esta
         * pantalla — aunque esa memoria sea efímera y no se guarde en ningún
         * lado.
         */}
        <ProbarElBot key={tenantId} tenantId={tenantId} />
      </div>
    </AppShell>
  );
}
